import type { ReactNode } from 'react'
import type { AnalyticsSummary, SummaryDimensionRow } from '../../../shared/api/analytics.api'
import { fmtInt, fmtRubShort } from './summaryFormat'

interface Bar {
  key: string
  ratio: number
  /** Доля позиций, ушедших в заказ (0..1) — светлая часть полосы (предложение заказчика 02.09). */
  orderedShare?: number
  /** Тултип полосы — расшифровка разбиения «в заказ / всего». */
  barTitle?: string
  main: string
  sub?: string
}

function BarList({ bars, color }: { bars: Bar[]; color: string }) {
  if (bars.length === 0) return <p className="text-sm text-gray-400">Нет данных за период</p>
  return (
    <div className="space-y-1.5">
      {bars.map((b) => (
        <div key={b.key} className="flex items-center gap-3">
          <div className="w-36 text-sm text-gray-700 flex-shrink-0 truncate" title={b.key}>
            {b.key}
          </div>
          <div className="flex-1 bg-gray-100 rounded-md h-6 overflow-hidden" title={b.barTitle}>
            <div
              className="h-full rounded-md overflow-hidden flex transition-all"
              style={{ width: `${Math.max(2, b.ratio * 100)}%` }}
            >
              {b.orderedShare != null ? (
                <>
                  <div
                    className="h-full"
                    style={{
                      width: `${(1 - b.orderedShare) * 100}%`,
                      backgroundColor: color,
                    }}
                  />
                  {/* Светлая часть — позиции, ушедшие в заказ (тот же цвет с прозрачностью). */}
                  <div
                    className="h-full"
                    style={{ width: `${b.orderedShare * 100}%`, backgroundColor: `${color}55` }}
                  />
                </>
              ) : (
                <div className="h-full w-full" style={{ backgroundColor: color }} />
              )}
            </div>
          </div>
          <div className="w-32 text-right text-xs text-gray-500 flex-shrink-0 tabular-nums">
            <span className="font-medium text-gray-800">{b.main}</span>
            {b.sub && <span className="text-gray-400"> · {b.sub}</span>}
          </div>
        </div>
      ))}
    </div>
  )
}

function Panel({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: ReactNode
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      <div className="mt-3">{children}</div>
    </div>
  )
}

/** Топ по объёму (заявок/позиций); подпись — ₽ в заказ, если есть. */
function volumeBars(rows: SummaryDimensionRow[], limit: number): Bar[] {
  const top = [...rows].sort((a, b) => b.volume - a.volume).slice(0, limit)
  const max = Math.max(1, ...top.map((r) => r.volume))
  return top.map((r) => ({
    key: r.key,
    ratio: r.volume / max,
    orderedShare: r.volume > 0 ? r.orderedCount / r.volume : 0,
    barTitle: `В заказ: ${fmtInt(r.orderedCount)} из ${fmtInt(r.volume)} поз.`,
    // «114 (43)» = всего позиций (из них в заказ) — просьба заказчика 02.09.
    main: r.orderedCount > 0 ? `${fmtInt(r.volume)} (${fmtInt(r.orderedCount)})` : fmtInt(r.volume),
    sub: r.orderedSum > 0 ? fmtRubShort(r.orderedSum) : undefined,
  }))
}

/** Топ по сумме в заказ (₽); подпись — число позиций в заказе. */
function orderedBars(rows: SummaryDimensionRow[], limit: number): Bar[] {
  const top = [...rows]
    .filter((r) => r.orderedSum > 0)
    .sort((a, b) => b.orderedSum - a.orderedSum)
    .slice(0, limit)
  const max = Math.max(1, ...top.map((r) => r.orderedSum))
  return top.map((r) => ({
    key: r.key,
    ratio: r.orderedSum / max,
    main: fmtRubShort(r.orderedSum),
    sub: `${fmtInt(r.orderedCount)} поз`,
  }))
}

export function SummaryDimensions({ data }: { data: AnalyticsSummary }) {
  return (
    <div className="space-y-4">
      {/* Что приносит деньги: сегменты клиентов vs сегменты товаров */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel
          title="Топ-10 сегментов клиентов в заказ"
          subtitle="Направления бизнеса клиентов, ушедшие в заказ (₽)"
        >
          <BarList bars={orderedBars(data.segments, 10)} color="#10b981" />
        </Panel>
        <Panel
          title="Топ-10 сегментов товаров в заказ"
          subtitle="Категории компонентов («Наименование»), ушедшие в заказ (₽)"
        >
          <BarList bars={orderedBars(data.categories, 10)} color="#f59e0b" />
        </Panel>
      </div>

      {/* Спрос: какие товары запрашивают и каких брендов */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel
          title="Топ-10 сегментов товаров по спросу"
          subtitle="Позиций в заявках периода: всего (из них в заказ); светлая часть полосы и ₽ — по заказам"
        >
          <BarList bars={volumeBars(data.categories, 10)} color="#6366f1" />
        </Panel>
        <Panel
          title="Топ-10 брендов"
          subtitle="Позиций за период: всего (из них в заказ); светлая часть полосы и ₽ — по заказам"
        >
          <BarList bars={volumeBars(data.brands, 10)} color="#8b5cf6" />
        </Panel>
      </div>

      <Panel title="Матрицы поставки" subtitle="Сумма в заказ по каналам (позиций всего / в заказ)">
        <BarList
          bars={[...data.matrices].map((r) => ({
            key: r.key,
            ratio: r.orderedSum / Math.max(1, ...data.matrices.map((m) => m.orderedSum)),
            main: fmtRubShort(r.orderedSum),
            sub: `${fmtInt(r.orderedCount)}/${fmtInt(r.volume)} поз`,
          }))}
          color="#0ea5e9"
        />
      </Panel>
    </div>
  )
}
